import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  where, 
  serverTimestamp,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useCallback, useEffect, useState } from 'react';
import { Worksheet, CreateWorksheetData, WorksheetSignature, WorksheetItem, WorksheetTask, WorksheetStatusChange, WorksheetStatus, WorksheetNote, WorksheetEquipment, WorksheetTemplate, WorksheetCustomField, WorksheetCustomStatus, calculateItemTotal } from '../types/worksheet';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { timeTrackingService } from '../lib/services/timeTrackingService';
import { worksheetTemplateService } from '../lib/services/worksheetTemplateService';
import toast from 'react-hot-toast';

export const useWorksheets = () => {
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { canAdmin, isSuperAdmin } = usePermissions();

  // Generate worksheet number
  const generateWorksheetNumber = useCallback(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0');
    return `WS-${year}${month}${day}-${time}`;
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const worksheetsRef = collection(db, 'worksheets');
    let q = query(worksheetsRef, orderBy('createdAt', 'desc'));

    // A super_admin minden munkalapot láthat
    // Más szerepkörök esetén nincs előzetes szűrés, mert a munkalapoknál
    // az organizationId opcionális, így a betöltés után szűrjük
    // ha szükséges a jogosultságok alapján

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        try {
          const loadedWorksheets = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              dueDate: data.dueDate?.toDate() || null,
              worksheetDate: data.worksheetDate?.toDate() || null,
              deploymentDate: data.deploymentDate?.toDate() || null,
              completedAt: data.completedAt?.toDate() || null,
              signatures: data.signatures?.map((sig: any) => ({
                ...sig,
                signedAt: sig.signedAt?.toDate() || new Date()
              })) || []
            } as Worksheet;
          });

          // Szűrés jogosultságok alapján
          let filteredWorksheets = loadedWorksheets;
          
          // Ha a felhasználó nem super_admin és nem admin a worksheets modulban,
          // és van organizationId, akkor szűrünk
          if (!isSuperAdmin && !canAdmin('worksheets') && user?.organizationId) {
            filteredWorksheets = loadedWorksheets.filter(worksheet => 
              worksheet.organizationId === user.organizationId
            );
          }

          setWorksheets(filteredWorksheets);
          setError(null);
          setLoading(false);
        } catch (err) {
          console.error('Error processing worksheets:', err);
          setError(err as Error);
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error loading worksheets:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, isSuperAdmin, canAdmin]);

  const addWorksheet = useCallback(async (worksheetData: CreateWorksheetData) => {
    try {
      const worksheetsRef = collection(db, 'worksheets');
      const worksheetNumber = generateWorksheetNumber();
      
      // Remove undefined values to prevent Firestore errors
      const cleanData = Object.fromEntries(
        Object.entries(worksheetData).filter(([, v]) => v !== undefined)
      );
      
      const newWorksheet = {
        ...cleanData,
        worksheetNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: user?.organizationId || '',
        signatures: [],
        documents: []
      };

      const docRef = await addDoc(worksheetsRef, newWorksheet);
      return docRef.id;
    } catch (err) {
      console.error('Error adding worksheet:', err);
      throw err;
    }
  }, [user, generateWorksheetNumber]);

  const updateWorksheet = useCallback(async (id: string, updateData: Partial<CreateWorksheetData>) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      const dataToUpdate: any = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      if (updateData.dueDate) {
        dataToUpdate.dueDate = Timestamp.fromDate(updateData.dueDate);
      }

      await updateDoc(worksheetRef, dataToUpdate);
    } catch (err) {
      console.error('Error updating worksheet:', err);
      throw err;
    }
  }, []);

  const deleteWorksheet = useCallback(async (id: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      await deleteDoc(worksheetRef);
    } catch (err) {
      console.error('Error deleting worksheet:', err);
      throw err;
    }
  }, []);

  const addSignature = useCallback(async (worksheetId: string, signature: Omit<WorksheetSignature, 'id'>) => {
    try {
      const worksheetRef = doc(db, 'worksheets', worksheetId);
      const worksheetSnap = await getDoc(worksheetRef);
      
      if (!worksheetSnap.exists()) {
        throw new Error('Worksheet not found');
      }

      const wsData = worksheetSnap.data() as Worksheet;
      const newSignature = {
        ...signature,
        id: `sig_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        signedAt: Timestamp.fromDate(new Date()) // Use Timestamp.fromDate instead of serverTimestamp
      };

      const updatedSignatures = [...(wsData.signatures || []), newSignature];
      
      await updateDoc(worksheetRef, {
        signatures: updatedSignatures,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding signature:', err);
      throw err;
    }
  }, []);

  const removeSignature = useCallback(async (worksheetId: string, signatureId: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', worksheetId);
      const worksheet = worksheets.find(w => w.id === worksheetId);
      
      if (!worksheet) {
        throw new Error('Worksheet not found');
      }

      const updatedSignatures = worksheet.signatures?.filter(sig => sig.id !== signatureId) || [];
      
      await updateDoc(worksheetRef, {
        signatures: updatedSignatures,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error removing signature:', err);
      throw err;
    }
  }, [worksheets]);

  const completeWorksheet = useCallback(async (id: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      await updateDoc(worksheetRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: user?.uid || '',
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error completing worksheet:', err);
      throw err;
    }
  }, [user]);

  // ============================================
  // NEW: Status change with history
  // ============================================
  const changeStatus = useCallback(async (id: string, newStatus: WorksheetStatus, reason?: string) => {
    try {
      const worksheet = worksheets.find(w => w.id === id);
      if (!worksheet) throw new Error('Worksheet not found');
      
      const statusChange: WorksheetStatusChange = {
        id: `sc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        fromStatus: worksheet.status,
        toStatus: newStatus,
        changedBy: user?.uid || '',
        changedByName: user?.displayName || '',
        changedAt: Timestamp.fromDate(new Date()),
        reason: reason || '',
      };
      
      const history = [...(worksheet.statusHistory || []), statusChange];
      
      await updateDoc(doc(db, 'worksheets', id), {
        status: newStatus,
        statusHistory: history,
        updatedAt: serverTimestamp(),
      });
      toast.success('Státusz frissítve');
    } catch (err) {
      console.error('Error changing status:', err);
      toast.error('Hiba a státusz váltáskor');
      throw err;
    }
  }, [worksheets, user]);

  // ============================================
  // NEW: Items management
  // ============================================
  const updateItems = useCallback(async (id: string, items: WorksheetItem[]) => {
    try {
      await updateDoc(doc(db, 'worksheets', id), {
        items,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating items:', err);
      throw err;
    }
  }, []);

  // ============================================
  // NEW: Tasks (checklist) management
  // ============================================
  const updateTasks = useCallback(async (id: string, tasks: WorksheetTask[]) => {
    try {
      await updateDoc(doc(db, 'worksheets', id), {
        tasks,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating tasks:', err);
      throw err;
    }
  }, []);

  const toggleTask = useCallback(async (worksheetId: string, taskId: string) => {
    try {
      const worksheet = worksheets.find(w => w.id === worksheetId);
      if (!worksheet || !worksheet.tasks) return;
      
      const updatedTasks = worksheet.tasks.map(t =>
        t.id === taskId
          ? { ...t, isCompleted: !t.isCompleted, completedAt: !t.isCompleted ? Timestamp.fromDate(new Date()) as any : undefined, completedBy: !t.isCompleted ? user?.uid : undefined }
          : t
      );
      
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        tasks: updatedTasks,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error toggling task:', err);
      throw err;
    }
  }, [worksheets, user]);

  // ============================================
  // NEW: Notes management
  // ============================================
  const addNote = useCallback(async (worksheetId: string, text: string, visibility: 'customer' | 'internal') => {
    try {
      const worksheet = worksheets.find(w => w.id === worksheetId);
      if (!worksheet) throw new Error('Worksheet not found');
      
      const note: WorksheetNote = {
        id: `note_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        text,
        visibility,
        createdBy: user?.uid || '',
        createdByName: user?.displayName || '',
        createdAt: Timestamp.fromDate(new Date()) as any,
      };
      
      const notes = [...(worksheet.notes_list || []), note];
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        notes_list: notes,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error adding note:', err);
      throw err;
    }
  }, [worksheets, user]);

  const removeNote = useCallback(async (worksheetId: string, noteId: string) => {
    try {
      const worksheet = worksheets.find(w => w.id === worksheetId);
      if (!worksheet) throw new Error('Worksheet not found');
      
      const notes = (worksheet.notes_list || []).filter(n => n.id !== noteId);
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        notes_list: notes,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error removing note:', err);
      throw err;
    }
  }, [worksheets]);

  // ============================================
  // NEW: Equipment management
  // ============================================
  const updateEquipment = useCallback(async (id: string, equipment: WorksheetEquipment[]) => {
    try {
      await updateDoc(doc(db, 'worksheets', id), {
        equipmentData: equipment,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating equipment:', err);
      throw err;
    }
  }, []);

  // ============================================
  // NEW: Time tracking
  // ============================================
  const startTimer = useCallback(async (worksheetId: string, taskId?: string) => {
    try {
      await timeTrackingService.startTimer(worksheetId, user?.uid || '', user?.displayName || '', taskId);
      toast.success('Időmérés elindítva');
    } catch (err) {
      console.error('Error starting timer:', err);
      toast.error('Hiba az időmérés indításakor');
      throw err;
    }
  }, [user]);

  const stopTimer = useCallback(async (entryId: string) => {
    try {
      await timeTrackingService.stopTimer(entryId);
      toast.success('Időmérés leállítva');
    } catch (err) {
      console.error('Error stopping timer:', err);
      toast.error('Hiba az időmérés leállításakor');
      throw err;
    }
  }, []);

  const addManualTime = useCallback(async (
    worksheetId: string,
    startTime: Date,
    endTime: Date,
    description?: string,
    workerCount?: number
  ) => {
    try {
      await timeTrackingService.addManualEntry(
        worksheetId,
        user?.uid || '',
        user?.displayName || '',
        startTime,
        endTime,
        description,
        workerCount
      );
      toast.success('Idő hozzáadva');
    } catch (err) {
      console.error('Error adding manual time:', err);
      toast.error('Hiba az idő hozzáadásakor');
      throw err;
    }
  }, [user]);

  // ============================================
  // NEW: Templates
  // ============================================
  const [templates, setTemplates] = useState<WorksheetTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (!user?.organizationId) return;
    setTemplatesLoading(true);
    try {
      const result = await worksheetTemplateService.getAll(user.organizationId);
      setTemplates(result);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  }, [user]);

  const createTemplate = useCallback(async (data: Omit<WorksheetTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      await worksheetTemplateService.create(data);
      toast.success('Sablon létrehozva');
      loadTemplates();
    } catch (err) {
      console.error('Error creating template:', err);
      toast.error('Hiba a sablon létrehozásakor');
      throw err;
    }
  }, [loadTemplates]);

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      await worksheetTemplateService.delete(id);
      toast.success('Sablon törölve');
      loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error('Hiba a sablon törlésekor');
      throw err;
    }
  }, [loadTemplates]);

  // ============================================
  // NEW: Create from template
  // ============================================
  const createFromTemplate = useCallback(async (templateId: string, customerId: string, title: string) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');
      
      const worksheetNumber = generateWorksheetNumber();
      const items: WorksheetItem[] = template.defaultItems.map((item, idx) => {
        const total = calculateItemTotal(item.quantity, item.unitPrice, item.vatRate, item.discount, item.discountType);
        return {
          ...item,
          id: `item_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
          totalNet: total.net,
          totalGross: total.gross,
          order: idx,
        };
      });
      
      const tasks: WorksheetTask[] = template.defaultTasks.map((task, idx) => ({
        ...task,
        id: `task_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
        order: idx,
      }));
      
      const newWorksheet = {
        title,
        customerId,
        templateId,
        status: 'draft' as WorksheetStatus,
        priority: template.defaultPriority || 'medium',
        workType: template.defaultWorkType || 'other',
        estimatedHours: template.estimatedHours,
        items,
        tasks,
        worksheetNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: user?.organizationId || '',
        signatures: [],
        documents: [],
        statusHistory: [],
      };
      
      const docRef = await addDoc(collection(db, 'worksheets'), newWorksheet);
      toast.success('Munkalap létrehozva sablonból');
      return docRef.id;
    } catch (err) {
      console.error('Error creating from template:', err);
      toast.error('Hiba a sablonból való létrehozáskor');
      throw err;
    }
  }, [templates, user, generateWorksheetNumber]);

  // ============================================
  // NEW: Bulk operations
  // ============================================
  const bulkChangeStatus = useCallback(async (ids: string[], newStatus: WorksheetStatus) => {
    try {
      await Promise.all(ids.map(id => {
        const worksheet = worksheets.find(w => w.id === id);
        if (!worksheet) return Promise.resolve();
        const statusChange: WorksheetStatusChange = {
          id: `sc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          fromStatus: worksheet.status,
          toStatus: newStatus,
          changedBy: user?.uid || '',
          changedByName: user?.displayName || '',
          changedAt: Timestamp.fromDate(new Date()),
          reason: '',
        };
        const history = [...(worksheet.statusHistory || []), statusChange];
        return updateDoc(doc(db, 'worksheets', id), {
          status: newStatus,
          statusHistory: history,
          updatedAt: serverTimestamp(),
        });
      }));
      toast.success(`${ids.length} munkalap státusza frissítve`);
    } catch (err) {
      console.error('Error bulk changing status:', err);
      toast.error('Hiba a tömeges státuszváltáskor');
      throw err;
    }
  }, [worksheets, user]);

  const bulkDelete = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => deleteDoc(doc(db, 'worksheets', id))));
      toast.success(`${ids.length} munkalap törölve`);
    } catch (err) {
      console.error('Error bulk deleting:', err);
      toast.error('Hiba a tömeges törléskor');
      throw err;
    }
  }, []);

  const exportToCsv = useCallback((worksheetsToExport: Worksheet[], getCustomerName: (id: string) => string) => {
    const headers = ['Sorszám', 'Cím', 'Ügyfél', 'Státusz', 'Típus', 'Prioritás', 'Helyszín', 'Határidő', 'Létrehozva'];
    const rows = worksheetsToExport.map(w => [
      w.worksheetNumber,
      w.title,
      getCustomerName(w.customerId),
      w.status,
      w.workType,
      w.priority,
      w.location || '',
      w.dueDate ? new Date(w.dueDate).toLocaleDateString('hu-HU') : '',
      new Date(w.createdAt).toLocaleDateString('hu-HU'),
    ]);
    const csv = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')
    ).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `munkalapok_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV export kész');
  }, []);

  // ============================================
  // NEW: Public link
  // ============================================
  const generatePublicLink = useCallback(async (worksheetId: string) => {
    try {
      const token = `pk_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      const link = `${window.location.origin}/worksheets/public/${token}`;
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        publicLink: link,
        publicLinkToken: token,
        updatedAt: serverTimestamp(),
      });
      return link;
    } catch (err) {
      console.error('Error generating public link:', err);
      toast.error('Hiba a publikus link generálásakor');
      throw err;
    }
  }, []);

  const getWorksheetByPublicToken = useCallback(async (token: string): Promise<Worksheet | null> => {
    try {
      const q = query(collection(db, 'worksheets'), where('publicLinkToken', '==', token));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const data = snapshot.docs[0].data();
      return {
        id: snapshot.docs[0].id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate() || null,
        worksheetDate: data.worksheetDate?.toDate() || null,
        deploymentDate: data.deploymentDate?.toDate() || null,
        completedAt: data.completedAt?.toDate() || null,
        signatures: data.signatures?.map((sig: any) => ({
          ...sig,
          signedAt: sig.signedAt?.toDate() || new Date()
        })) || []
      } as Worksheet;
    } catch (err) {
      console.error('Error fetching worksheet by public token:', err);
      return null;
    }
  }, []);

  // ============================================
  // NEW: Conversions
  // ============================================
  const convertToQuotation = useCallback(async (worksheetId: string): Promise<string | null> => {
    try {
      const worksheet = worksheets.find(w => w.id === worksheetId);
      if (!worksheet) return null;
      
      const quotationNumber = `AJ-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
      const items = (worksheet.items || []).filter(i => i.type !== 'header').map(item => ({
        productId: item.productId || '',
        productName: item.name,
        productCode: '',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        price: item.unitPrice * item.quantity,
        taxRate: item.vatRate ?? 27,
        discount: item.discount ?? 0,
        lineTotal: item.totalNet ?? item.unitPrice * item.quantity,
        lineTotalWithTax: item.totalGross ?? item.unitPrice * item.quantity * 1.27,
        currency: 'HUF',
      }));
      
      const subtotal = items.reduce((sum, i) => sum + (i.lineTotal || 0), 0);
      const taxAmount = items.reduce((sum, i) => sum + ((i.lineTotalWithTax || 0) - (i.lineTotal || 0)), 0);
      
      const quotationData: Record<string, any> = {
        status: 'draft',
        number: quotationNumber,
        organizationId: worksheet.organizationId || user?.organizationId || '',
        customerId: worksheet.customerId,
        date: new Date().toISOString(),
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        paymentDue: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString(),
        subtotal,
        taxAmount,
        totalAmount: subtotal + taxAmount,
        currency: 'HUF',
        items,
        notes: worksheet.description || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user?.uid || '',
      };
      
      // Remove undefined values
      Object.keys(quotationData).forEach(key => {
        if (quotationData[key] === undefined) delete quotationData[key];
      });
      
      const docRef = await addDoc(collection(db, 'quotations'), quotationData);
      
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        convertedTo: [...(worksheet.convertedTo || []), { type: 'quotation' as const, id: docRef.id, number: quotationNumber }],
        updatedAt: serverTimestamp(),
      });
      
      toast.success('Árajánlat létrehozva munkalapból');
      return docRef.id;
    } catch (err) {
      console.error('Error converting to quotation:', err);
      toast.error('Hiba az árajánlat létrehozásakor');
      return null;
    }
  }, [worksheets, user]);

  const convertFromQuotation = useCallback(async (quotationId: string, quotationItems: any[], customerId: string, title: string): Promise<string | null> => {
    try {
      const worksheetNumber = generateWorksheetNumber();
      const items: WorksheetItem[] = quotationItems.map((item, idx) => {
        const total = calculateItemTotal(item.quantity, item.unitPrice, item.taxRate || 27, item.discount);
        return {
          id: `item_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
          type: 'other' as const,
          name: item.productName,
          quantity: item.quantity,
          unit: 'db',
          unitPrice: item.unitPrice,
          vatRate: item.taxRate || 27,
          discount: item.discount,
          totalNet: total.net,
          totalGross: total.gross,
          order: idx,
          productId: item.productId || '',
        };
      });
      
      const newWorksheet = {
        title,
        customerId,
        status: 'draft' as WorksheetStatus,
        priority: 'medium' as const,
        workType: 'other' as const,
        items,
        worksheetNumber,
        convertedFrom: { type: 'quotation' as const, id: quotationId },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: user?.organizationId || '',
        signatures: [],
        documents: [],
        statusHistory: [],
      };
      
      const docRef = await addDoc(collection(db, 'worksheets'), newWorksheet);
      toast.success('Munkalap létrehozva árajánlatból');
      return docRef.id;
    } catch (err) {
      console.error('Error converting from quotation:', err);
      toast.error('Hiba a munkalap létrehozásakor');
      return null;
    }
  }, [user, generateWorksheetNumber]);

  // ============================================
  // NEW: Convert survey (inspection worksheet) → worksheet
  // ============================================
  const convertFromSurvey = useCallback(async (surveyWorksheetId: string): Promise<string | null> => {
    try {
      const surveyWs = worksheets.find(w => w.id === surveyWorksheetId);
      if (!surveyWs) {
        toast.error('Felmérés nem található');
        return null;
      }

      const worksheetNumber = generateWorksheetNumber();
      
      // Copy items from survey, reset stockDeducted
      const items: WorksheetItem[] = (surveyWs.items || []).map((item, idx) => ({
        ...item,
        id: `item_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
        stockDeducted: false,
        order: idx,
      }));

      // Copy tasks from survey, reset completion
      const tasks: WorksheetTask[] = (surveyWs.tasks || []).map(task => ({
        ...task,
        id: `task_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        isCompleted: false,
        completedAt: undefined,
        completedBy: undefined,
      }));

      const newWorksheet: Record<string, any> = {
        title: `${surveyWs.title} (Munkalap)`,
        description: surveyWs.description || '',
        customerId: surveyWs.customerId,
        status: 'draft' as WorksheetStatus,
        priority: surveyWs.priority || 'medium',
        workType: 'other' as const,
        location: surveyWs.location || '',
        items,
        tasks,
        worksheetNumber,
        convertedFrom: { type: 'survey' as const, id: surveyWorksheetId, number: surveyWs.worksheetNumber },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: surveyWs.organizationId || user?.organizationId || '',
        signatures: [],
        documents: [],
        statusHistory: [],
        notes_list: [],
      };

      // Remove undefined values
      Object.keys(newWorksheet).forEach(key => {
        if (newWorksheet[key] === undefined) delete newWorksheet[key];
      });

      const docRef = await addDoc(collection(db, 'worksheets'), newWorksheet);

      // Mark survey worksheet as converted
      await updateDoc(doc(db, 'worksheets', surveyWorksheetId), {
        convertedTo: [...(surveyWs.convertedTo || []), { type: 'worksheet' as const, id: docRef.id, number: worksheetNumber }],
        updatedAt: serverTimestamp(),
      });

      toast.success('Munkalap létrehozva felmérésből');
      return docRef.id;
    } catch (err) {
      console.error('Error converting from survey:', err);
      toast.error('Hiba a munkalap létrehozásakor');
      return null;
    }
  }, [worksheets, user, generateWorksheetNumber]);

  // ============================================
  // NEW: Stock deduction on status change
  // ============================================
  const deductStockForWorksheet = useCallback(async (worksheetId: string) => {
    try {
      const worksheet = worksheets.find(w => w.id === worksheetId);
      if (!worksheet || !worksheet.items) return;
      
      const materialItems = worksheet.items.filter(i => i.type === 'material' && i.productId && !i.stockDeducted);
      if (materialItems.length === 0) return;
      
      for (const item of materialItems) {
        if (!item.productId) continue;
        const inventoriesQuery = query(
          collection(db, 'inventory'),
          where('productId', '==', item.productId)
        );
        const invSnapshot = await getDocs(inventoriesQuery);
        if (invSnapshot.empty) continue;
        
        const invDoc = invSnapshot.docs[0];
        const invData = invDoc.data();
        const newQuantity = Math.max(0, (invData.quantity || 0) - item.quantity);
        
        await updateDoc(doc(db, 'inventory', invDoc.id), {
          quantity: newQuantity,
          lastUpdated: serverTimestamp(),
          updatedBy: user?.uid || '',
        });
        
        await addDoc(collection(db, 'inventoryMovements'), {
          productId: item.productId,
          productCode: invData.productCode || '',
          productName: item.name,
          warehouseId: invData.warehouseId || '',
          warehouseName: invData.warehouseName || '',
          quantity: item.quantity,
          unit: item.unit,
          date: new Date(),
          type: 'STOCK_OUT',
          documentId: worksheetId,
          documentType: 'worksheet',
          documentNumber: worksheet.worksheetNumber,
          userId: user?.uid || '',
          userName: user?.displayName || '',
          note: `Munkalapból levonás: ${worksheet.worksheetNumber}`,
          isActive: true,
          createdAt: serverTimestamp(),
        });
      }
      
      const updatedItems = worksheet.items.map(i => 
        i.type === 'material' && i.productId ? { ...i, stockDeducted: true } : i
      );
      
      await updateDoc(doc(db, 'worksheets', worksheetId), {
        items: updatedItems,
        stockDeductedStatus: worksheet.status,
        stockDeductedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      toast.success('Készletlevonás sikeres');
    } catch (err) {
      console.error('Error deducting stock:', err);
      toast.error('Hiba a készletlevonáskor');
    }
  }, [worksheets, user]);

  // ============================================
  // NEW: Custom fields & statuses
  // ============================================
  const [customFields, setCustomFields] = useState<WorksheetCustomField[]>([]);
  const [customStatuses, setCustomStatuses] = useState<WorksheetCustomStatus[]>([]);

  const loadCustomFields = useCallback(async () => {
    if (!user?.organizationId) return;
    try {
      const q = query(collection(db, 'worksheetCustomFields'), where('organizationId', '==', user.organizationId));
      const snapshot = await getDocs(q);
      setCustomFields(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorksheetCustomField)));
    } catch (err) {
      console.error('Error loading custom fields:', err);
    }
  }, [user]);

  const saveCustomField = useCallback(async (field: Omit<WorksheetCustomField, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'worksheetCustomFields'), {
        ...field,
        createdAt: serverTimestamp(),
      });
      toast.success('Egyedi mező létrehozva');
      loadCustomFields();
    } catch (err) {
      console.error('Error saving custom field:', err);
      toast.error('Hiba az egyedi mező mentésekor');
    }
  }, [loadCustomFields]);

  const deleteCustomField = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'worksheetCustomFields', id));
      toast.success('Egyedi mező törölve');
      loadCustomFields();
    } catch (err) {
      console.error('Error deleting custom field:', err);
    }
  }, [loadCustomFields]);

  const loadCustomStatuses = useCallback(async () => {
    if (!user?.organizationId) return;
    try {
      const q = query(collection(db, 'worksheetCustomStatuses'), where('organizationId', '==', user.organizationId));
      const snapshot = await getDocs(q);
      setCustomStatuses(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorksheetCustomStatus)));
    } catch (err) {
      console.error('Error loading custom statuses:', err);
    }
  }, [user]);

  const saveCustomStatus = useCallback(async (status: Omit<WorksheetCustomStatus, 'id'>) => {
    try {
      await addDoc(collection(db, 'worksheetCustomStatuses'), status);
      toast.success('Saját státusz létrehozva');
      loadCustomStatuses();
    } catch (err) {
      console.error('Error saving custom status:', err);
      toast.error('Hiba a saját státusz mentésekor');
    }
  }, [loadCustomStatuses]);

  const deleteCustomStatus = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'worksheetCustomStatuses', id));
      toast.success('Saját státusz törölve');
      loadCustomStatuses();
    } catch (err) {
      console.error('Error deleting custom status:', err);
    }
  }, [loadCustomStatuses]);

  // ============================================
  // NEW: Deployments (keret-alkalom)
  // ============================================
  const createDeployment = useCallback(async (parentWorksheetId: string, deploymentData: { title: string; worksheetDate?: Date; startTime?: string; endTime?: string }) => {
    try {
      const parent = worksheets.find(w => w.id === parentWorksheetId);
      if (!parent) throw new Error('Parent worksheet not found');
      
      const worksheetNumber = `${parent.worksheetNumber}/${String((parent.convertedTo || []).length + 1)}`;
      const newDeployment = {
        title: deploymentData.title,
        customerId: parent.customerId,
        status: 'draft' as WorksheetStatus,
        priority: parent.priority,
        workType: parent.workType,
        parentWorksheetId,
        isParent: false,
        worksheetNumber,
        worksheetDate: deploymentData.worksheetDate || null,
        startTime: deploymentData.startTime || '',
        endTime: deploymentData.endTime || '',
        location: parent.location || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: user?.organizationId || '',
        signatures: [],
        documents: [],
        statusHistory: [],
        items: [],
        tasks: [],
      };
      
      const docRef = await addDoc(collection(db, 'worksheets'), newDeployment);
      
      await updateDoc(doc(db, 'worksheets', parentWorksheetId), {
        isParent: true,
        updatedAt: serverTimestamp(),
      });
      
      toast.success('Kiszállás létrehozva');
      return docRef.id;
    } catch (err) {
      console.error('Error creating deployment:', err);
      toast.error('Hiba a kiszállás létrehozásakor');
      throw err;
    }
  }, [worksheets, user]);

  const getDeployments = useCallback((parentWorksheetId: string): Worksheet[] => {
    return worksheets.filter(w => w.parentWorksheetId === parentWorksheetId);
  }, [worksheets]);

  return {
    worksheets,
    loading,
    error,
    addWorksheet,
    updateWorksheet,
    deleteWorksheet,
    addSignature,
    removeSignature,
    completeWorksheet,
    // Status & items
    changeStatus,
    updateItems,
    updateTasks,
    toggleTask,
    addNote,
    removeNote,
    updateEquipment,
    startTimer,
    stopTimer,
    addManualTime,
    // Templates
    templates,
    templatesLoading,
    loadTemplates,
    createTemplate,
    deleteTemplate,
    createFromTemplate,
    // Bulk operations
    bulkChangeStatus,
    bulkDelete,
    exportToCsv,
    // Public link
    generatePublicLink,
    getWorksheetByPublicToken,
    // Conversions
    convertToQuotation,
    convertFromQuotation,
    convertFromSurvey,
    // Stock deduction
    deductStockForWorksheet,
    // Custom fields & statuses
    customFields,
    customStatuses,
    loadCustomFields,
    saveCustomField,
    deleteCustomField,
    loadCustomStatuses,
    saveCustomStatus,
    deleteCustomStatus,
    // Deployments
    createDeployment,
    getDeployments,
  };
}; 